import {
  FaceItemModel,
  Facet,
  RangeValues,
  RoomFilter,
  TournamentTagsEnum,
  TournamentType,
  type Game,
} from '@fantaz/shared/data-access/models';
import { getFilterRangesByList } from '@fantaz/shared/utils/filters';

export type FilterOptions = {
  title: string;
  key: string;
};

export type FilterItems = FilterOptions[];

export type FilterGroup<T extends string | number | boolean | object> = {
  title: string;
  key: string;
  items: any[];
  minSelected?: number;
};

export type ValueRange = {
  start: number;
  end: number;
};

export type SelectedFilerValues = Record<string, string | string[]>;

/**
 * Returns the URL of the game's avatar image.
 * @param game - The game object.
 * @returns The URL of the game's avatar image.
 */
function getGameImageUrl(game: Game): string {
  const result = game.key.split('.');
  const [gameName, version] = result || ['default', 'v1'];

  return `/shared/img/games/${gameName}/${version}/${gameName}-avatar.webp`;
}

/**
 * Transforms a tag string by removing the word "tournament" and converting it to lowercase.
 * @param {string} tag - The tag string to transform.
 * @returns {string} The transformed tag string.
 */
function transformTag(tag: string): string {
  return tag.replace(/tournament/gi, '').trim();
}

/**
 * Creates an array of entry amount filter options.
 * @param {Array<any>} entryAmounts - An array of entry amount objects.
 * @returns An array of entry amount filter options.
 */
export function createEntryAmountFilterOptions(entryAmounts: any[]): Facet {
  return {
    entryAmount: {
      label: 'Entry Fee',
      items: entryAmounts.map((el) => ({
        group: el.entryAmount,
        label: `${el.label} USD`,
        key: el.key,
        value: el,
      })),
    },
  };
}

/**
 * Creates an array of prize filter options.
 * @param {Array<any>} prizes - An array of prize objects.
 * @returns An array of prize filter options.
 */
export function createPrizeFilterOptions(prizes: any[]): Facet {
  return {
    prize: {
      label: 'Prize',
      items: prizes.map((el) => ({
        group: el.prize,
        label: `${el.label} USD`,
        key: el.key,
        value: el,
      })),
    },
  };
}

/**
 * Creates an array of game filter options.
 * @param {Array<Game>} games - An array of game objects.
 * @returns An array of game filter options.
 */
export function createGameFilterOptions(games: FaceItemModel[]): Facet {
  return {
    games: {
      label: 'Games',
      items: games,
    },
  };
}

/**
 * Creates an array of status filter options.
 * @returns An array of status filter options.
 */
export function createStatusFilterOptions(status: FaceItemModel[]): Facet {
  return {
    status: {
      label: 'Status',
      items: status,
    },
  };
}

export function createTournamentTypeFilterOptions(
  types: FaceItemModel[],
): Facet {
  return {
    type: {
      label: 'Tournament type',
      items: types,
    },
  };
}

export function createTagsFilterOptions(
  tags: FaceItemModel[],
  exclude: FaceItemModel[],
): Facet {
  return {
    tags: {
      label: 'Recommended categories',
      items: tags.filter(
        (tag) => !exclude.map((el) => el.key).includes(tag.key),
      ),
    },
  };
}

export function classifyTags(tags: Record<string, boolean>) {
  const selectedTags = Object.keys(tags).filter((tag) => tags[tag]) as string[];
  const allowedTournamentTypes = [
    TournamentType.CASH,
    TournamentType.PRIZE,
  ] as string[];
  const allowedTags = [
    TournamentTagsEnum.CELEBRITY_EVENT,
    TournamentTagsEnum.CHARITY_EVENT,
    TournamentTagsEnum.FREE_TOURNAMENT,
    TournamentTagsEnum.ZBO_TOURNAMENT,
  ] as string[];
  const tournamentTypes = selectedTags.filter((tag) =>
    allowedTournamentTypes.includes(tag),
  );

  return {
    tags: selectedTags.filter((tag) => allowedTags.includes(tag)),
    tournamentType:
      tournamentTypes.length > 1
        ? TournamentType.ALL
        : (tournamentTypes[0] as TournamentType),
  };
}

export function getTournamentType(types: string[]): TournamentType {
  if (types.length > 1) {
    return TournamentType.ALL;
  }

  if (types.length === 0) {
    return TournamentType.CASH;
  }

  return types[0] as TournamentType;
}

/**
 * Maps the tournament type to the corresponding filter value.
 * By default, returns TournamentType.PRIZE.
 *
 * @param tournamentType The tournament type.
 * @returns The corresponding filter value.
 */
export function mapTournamentType(tournamentType: string[]) {
  if (tournamentType.length > 1) {
    return {
      tournamentType: TournamentType.ALL,
    };
  }

  if (tournamentType.length === 1) {
    return {
      tournamentType: tournamentType[0] as TournamentType,
    };
  }

  return {
    tournamentType: TournamentType.PRIZE,
  };
}

/**
 * Maps the tournament status to the corresponding filter value.
 * By default, returns active tournaments.
 *
 * @param status The tournament status.
 * @returns The corresponding filter value.
 */
export function mapTournamentStatus(status: string[]) {
  status = status.map((s) => s.toLowerCase());
  return {
    ...(status.includes('active') ? { activeTournaments: true } : {}),
    ...(status.includes('coming-soon') ? { comingSoonOnly: true } : {}),
    ...(status.includes('ended') ? { retiredEvents: true } : {}),
  };
}

export function mapTournamentTags(tags: string[]) {
  return {
    tournamentTags: tags as TournamentTagsEnum[],
  };
}

export function mapTournamentGame(games: string[]) {
  return {
    gameIds: games,
  };
}

export function mapTournamentEntryAmount(entryFeeRanges: string[]) {
  return {
    entryFeeRanges,
  };
}

export function mapTournamentPrize(prizeValueRanges: string[]) {
  return {
    prizeValueRanges,
  };
}

export function createValues(element: number[]): RoomFilter[] {
  const result: RoomFilter[] = [];
  const filterRanges = getFilterRangesByList(element, { symbol: '$' });

  for (const element in filterRanges) {
    result.push({
      key: filterRanges[element].key,
      label: `${filterRanges[element].name}`,
      type: `${filterRanges[element].type}`,
      selected: false,
      value: {
        start: filterRanges[element].values[0],
        end: filterRanges[element].values[1],
      },
    });
  }

  return result;
}

export function createFilters(ranges: RangeValues[]) {
  const values: number[] = [];

  ranges.forEach((range) => {
    values.push(...range.values);
  });

  return createValues(values);
}
